import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useAuth } from "@clerk/react";
import Select from "react-select";

const selectStyles = {
  container: (base) => ({ ...base, width: "100%" }),
  control: (base, state) => ({
    ...base,
    fontSize: 13,
    borderRadius: 6,
    borderColor: state.isFocused ? "#232231" : "#e5e7eb",
    boxShadow: "none",
    "&:hover": { borderColor: "#9ca3af" },
    minHeight: 36,
    background: "#fff",
    cursor: "pointer",
  }),
  placeholder: (base) => ({ ...base, fontSize: 13, color: "#9ca3af" }),
  singleValue: (base) => ({ ...base, fontSize: 13, color: "#111827", fontWeight: 500 }),
  option: (base, { isFocused, isSelected }) => ({
    ...base,
    backgroundColor: isSelected ? "#232231" : isFocused ? "#f3f4f6" : "#fff",
    color: isSelected ? "#fff" : "#111827",
    fontSize: 13,
    padding: "8px 12px",
    cursor: "pointer",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    borderRadius: 6,
    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
    border: "1px solid #e5e7eb",
    overflow: "hidden",
  }),
  menuList: (base) => ({ ...base, padding: 4 }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({ ...base, color: "#9ca3af", padding: "4px 8px" }),
  clearIndicator: (base) => ({ ...base, color: "#9ca3af", padding: "4px 6px" }),
};

function ProjectSelector({ onProjectSelect, onProjectChange, authToken, onLoaded }) {
  const { getToken } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [error, setError] = useState(null);
  const didFetchRef = useRef(false);
  const autoSelectedRef = useRef(false);

  useEffect(() => {
    if (didFetchRef.current) return;
    didFetchRef.current = true;

    const load = async () => {
      try {
        const token = authToken || await getToken();
        if (!token) {
          setError("Not authenticated.");
          return;
        }
        const res = await fetch(
          `${process.env.REACT_APP_API_URL || ""}/api/getProjects`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) {
          const txt = await res.text();
          throw new Error(`Fetch error: ${res.status} – ${txt}`);
        }
        const data = await res.json();
        if (Array.isArray(data.projects || data)) {
          const list = (data.projects || data).map((p) => ({
            value: p.id,
            label: p.name,
            fullData: p,
          }));
          setProjects(list);
          onLoaded?.(list.length);
        } else {
          throw new Error("Server returned invalid data.");
        }
      } catch (err) {
        console.error(err);
        setError(err.message || "Failed to fetch projects.");
        onLoaded?.(0);
      }
    };

    load();
  }, [getToken, authToken]);

  // Auto-select when there is exactly one project
  useEffect(() => {
    if (projects.length === 1 && !autoSelectedRef.current) {
      autoSelectedRef.current = true;
      setSelectedOption(projects[0]);
      onProjectChange?.();
      onProjectSelect(projects[0].fullData);
    }
  }, [projects, onProjectSelect, onProjectChange]);

  const handleChange = useCallback(
    (opt) => {
      onProjectChange?.();
      setSelectedOption(opt);
      onProjectSelect(opt?.fullData || null);
    },
    [onProjectChange, onProjectSelect]
  );

  if (error) {
    return (
      <div style={{ color: "#ef4444", fontSize: 12, marginBottom: 8 }}>
        {error}
      </div>
    );
  }

  // Single project: show name only, no dropdown
  if (projects.length === 1) {
    return (
      <div style={{
        width: "100%", marginBottom: 14,
        fontSize: 13, fontWeight: 500,
        padding: "0 10px", height: 36,
        display: "flex", alignItems: "center",
        background: "#fff", borderRadius: 6,
        border: "1px solid #e5e7eb",
        color: "#111827", boxSizing: "border-box",
        overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
      }}>
        {projects[0].label}
      </div>
    );
  }

  return (
    <div style={{ width: "100%", marginBottom: 14, textAlign: "left" }}>
      <label
        htmlFor="project-select"
        style={{
          position: "absolute", width: 1, height: 1, padding: 0,
          margin: -1, overflow: "hidden", clip: "rect(0,0,0,0)",
          whiteSpace: "nowrap", border: 0,
        }}
      >
        Project
      </label>
      <Select
        inputId="project-select"
        name="project"
        value={selectedOption}
        onChange={handleChange}
        options={projects}
        placeholder="Select a project…"
        isClearable
        styles={selectStyles}
        menuPlacement="bottom"
        menuPosition="absolute"
      />
    </div>
  );
}

export default React.memo(ProjectSelector);
